import type { QueryClient } from '@tanstack/react-query';
import {
  getClassicProjectRouteProjectId,
  getParseFlowFocusedProjectId,
  isOnParseFlowPage,
} from '@/lib/access-revocation';
import type { NotificationHandlerContext } from '@/lib/notification-side-effects';
import type { Project } from '@/types/project';

function getCachedProjectDetail(queryClient: QueryClient, projectId: string): Project | undefined {
  const entries = queryClient.getQueriesData<Project>({
    predicate: (query) => {
      const key = query.queryKey;
      return key[0] === 'projects' && key[1] === 'detail' && key.includes(projectId);
    },
  });
  return entries.find(([, data]) => data)?.[1];
}

/** 当前路由真正打开的项目。分镜台只认聚焦单集，不把 persist 的父项目算进去。 */
export function getCurrentPageProjectId(context: NotificationHandlerContext): string | null {
  const routeProjectId = getClassicProjectRouteProjectId(context.pathname);
  if (routeProjectId) {
    return routeProjectId;
  }
  const focusedProjectId = getParseFlowFocusedProjectId(context.pathname, context.search);
  if (focusedProjectId) {
    return focusedProjectId;
  }
  return context.currentProjectId;
}

/** 当前路由 / Parse Flow 聚焦范围内的项目 ID（不含通知事件中的其他单集）。 */
export function getRouteScopedProjectIds(context: NotificationHandlerContext): string[] {
  const ids = new Set<string>();
  const pageProjectId = getCurrentPageProjectId(context);
  if (pageProjectId) {
    ids.add(pageProjectId);
  }
  if (!isOnParseFlowPage(context.pathname) && context.currentProjectId) {
    ids.add(context.currentProjectId);
  }
  return [...ids];
}

/**
 * 判断在当前 activeOrgId 下是否应对该项目执行 getById 同步。
 * 依赖已缓存的项目详情（viewScope / orgId）；无缓存时保守跳过，避免 403。
 */
export function shouldSyncProjectDetailForIdentity(
  queryClient: QueryClient,
  projectId: string,
  activeOrgId: string | null
): boolean {
  const project = getCachedProjectDetail(queryClient, projectId);
  if (!project) {
    return false;
  }

  if (!activeOrgId) {
    return !project.orgId;
  }

  if (project.orgId && activeOrgId === project.orgId) {
    return true;
  }

  if (project.viewScope === 'external') {
    return Boolean(project.orgId && activeOrgId !== project.orgId);
  }

  return false;
}

/** 职能变更后仅同步当前页单集且在当前身份下可访问的项目详情。 */
export function filterProjectIdsForAuthChangeSync(
  projectIds: string[],
  context: NotificationHandlerContext,
  queryClient: QueryClient,
  activeOrgId: string | null
): string[] {
  const uniqueProjectIds = [...new Set(projectIds.filter(Boolean))];
  if (uniqueProjectIds.length === 0) {
    return [];
  }

  const routeScopedIds = getRouteScopedProjectIds(context);
  const routeCandidates =
    routeScopedIds.length > 0
      ? uniqueProjectIds.filter((projectId) => routeScopedIds.includes(projectId))
      : [];

  const pageProjectId = getCurrentPageProjectId(context);
  return routeCandidates.filter((projectId) => {
    if (projectId === pageProjectId) return true;
    return shouldSyncProjectDetailForIdentity(queryClient, projectId, activeOrgId);
  });
}
